import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, map, Observable, Subject, tap } from 'rxjs';
import {
    ListQuery,
    ListQueryBuilder,
    PagedQuery,
    PagedQueryBuilder,
} from '../../shared/commons/query';
import { Identifiable } from '../models/identifiable.type';

/**
 * Erro de comunicação remota, extraído do payload de erro da API.
 */
export class RemoteError extends Error {
    constructor(httpError: HttpErrorResponse) {
        const message = httpError.error.error.message;
        super(message);
    }
}

/**
 * Contrato de um serviço capaz de construir consultas (lista/paginada).
 */
export interface Queryable {
    createPagedQueryBuilder(): PagedQueryBuilder;

    createListQueryBuilder(): ListQueryBuilder;
}

/**
 * Contrato de um serviço capaz de buscar um recurso por identificador.
 */
export interface Retrievable<T extends Identifiable = Identifiable> {
    findById<R = T>(id: string): Observable<R>;
}

/**
 * Contrato de um serviço capaz de criar, atualizar e excluir recursos.
 */
export interface Modifiable<T extends Identifiable = Identifiable> {
    create<C = T>(data: C): Observable<C>;

    update<U = T>(data: U): Observable<U>;

    delete(id: string): Observable<void>;
}

/**
 * Base de serviços que apenas leem um recurso por id.
 *
 * Injeta `HttpClient` e `DestroyRef` via `inject()`, de modo que as
 * subclasses só precisam informar `baseUrl` e `path` ao `super()` — isso
 * elimina a antiga inconsistência de ordem dos parâmetros de construtor.
 *
 * @template T Tipo de domínio do recurso manipulado pelo serviço.
 */
export abstract class RetrievableService<T extends Identifiable = Identifiable>
    implements Retrievable<T>
{
    protected readonly httpClient = inject(HttpClient);
    protected readonly destroyRef = inject(DestroyRef);
    protected endpoint: string;

    protected constructor(
        protected baseUrl: string,
        protected path: string,
    ) {
        this.endpoint = `${baseUrl}/${path}`;
    }

    /**
     * Busca um único recurso pelo seu identificador.
     *
     * @param id Identificador do recurso.
     * @returns Observable que emite o recurso encontrado.
     */
    findById<R = T>(id: string): Observable<R> {
        return this.httpClient.get<unknown>(`${this.endpoint}/${id}`).pipe(
            map((raw) => this.mapFromWire(raw) as R),
            catchError((error: HttpErrorResponse) => this.handleError(error)),
        );
    }

    /**
     * Hook de mapeamento do formato de transporte (wire) para o domínio.
     * Sobrescreva para converter snake_case em camelCase, por exemplo.
     * Por padrão, é a identidade.
     */
    protected mapFromWire(raw: unknown): unknown {
        return raw;
    }

    /**
     * Hook de mapeamento do domínio para o formato de transporte (wire).
     * Por padrão, é a identidade.
     */
    protected mapToWire(data: unknown): unknown {
        return data;
    }

    /**
     * Hook de tratamento de erro HTTP. Por padrão lança {@link RemoteError}.
     */
    protected handleError(error: HttpErrorResponse): never {
        throw new RemoteError(error);
    }
}

/**
 * Base de serviços que, além de buscar por id, constroem consultas de
 * lista e paginadas a partir do `baseUrl`/`path` configurados.
 *
 * @template T Tipo de domínio do recurso manipulado pelo serviço.
 */
export abstract class QueryableService<T extends Identifiable = Identifiable>
    extends RetrievableService<T>
    implements Queryable
{
    /**
     * Cria um builder de consulta em lista (sem paginação) já apontado
     * para o endpoint do serviço.
     */
    createListQueryBuilder(): ListQueryBuilder {
        const builder = new ListQueryBuilder(this.httpClient, this.destroyRef);

        builder.baseUrl(this.baseUrl).path(this.path);

        return builder;
    }

    /**
     * Cria um builder de consulta paginada já apontado para o endpoint do
     * serviço, com `pageSize(5)` e sem pré-carregar a primeira página.
     */
    createPagedQueryBuilder(): PagedQueryBuilder {
        const builder = new PagedQueryBuilder(this.httpClient, this.destroyRef);

        builder
            .baseUrl(this.baseUrl)
            .path(this.path)
            .pageSize(5)
            .preFetchFirstPage(false);

        return builder;
    }

    /**
     * Atalho: retorna uma {@link ListQuery} já construída para o recurso.
     */
    list(): ListQuery<T> {
        return this.createListQueryBuilder().build<T>();
    }

    /**
     * Atalho: retorna uma {@link PagedQuery} já construída para o recurso.
     *
     * @param pageSize Tamanho da página (padrão 5).
     */
    paged(pageSize = 5): PagedQuery<T> {
        return this.createPagedQueryBuilder().pageSize(pageSize).build<T>();
    }
}

/**
 * Base de serviços CRUD-sobre-HTTP. Reúne leitura por id, consultas e as
 * mutações `create`/`update`/`delete`, emitindo em {@link onChanges} a cada
 * alteração para que telas reativas possam se atualizar.
 *
 * @template T Tipo de domínio do recurso manipulado pelo serviço.
 */
export abstract class CrudService<T extends Identifiable = Identifiable>
    extends QueryableService<T>
    implements Modifiable<T>
{
    private readonly changesSubject = new Subject<unknown>();
    private readonly changesObservable = this.changesSubject.asObservable();

    /**
     * Observable que emite a cada criação/atualização/exclusão.
     */
    onChanges(): Observable<unknown> {
        return this.changesObservable;
    }

    /**
     * Emite uma mudança para os assinantes de {@link onChanges}.
     */
    protected emitChange(value: unknown): void {
        this.changesSubject.next(value);
    }

    /**
     * Cria um novo recurso.
     *
     * @param data Dados do recurso a criar.
     * @returns Observable que emite o recurso criado.
     */
    create<C = T>(data: C): Observable<C> {
        return this.httpClient
            .post<unknown>(this.endpoint, this.mapToWire(data))
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                map((raw) => this.mapFromWire(raw) as C),
                tap((created) => this.emitChange(created)),
                catchError((error: HttpErrorResponse) =>
                    this.handleError(error),
                ),
            );
    }

    /**
     * Atualiza um recurso existente (identificado por `data.id`).
     *
     * @param data Dados do recurso a atualizar.
     * @returns Observable que emite o recurso atualizado.
     */
    update<U = T>(data: U): Observable<U> {
        return this.httpClient
            .put<unknown>(
                `${this.endpoint}/${(data as { id?: string }).id}`,
                this.mapToWire(data),
            )
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                map((raw) => this.mapFromWire(raw) as U),
                tap((updated) => this.emitChange(updated)),
                catchError((error: HttpErrorResponse) =>
                    this.handleError(error),
                ),
            );
    }

    /**
     * Exclui um recurso pelo seu identificador.
     *
     * @param id Identificador do recurso.
     * @returns Observable que completa quando a exclusão termina.
     */
    delete(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${this.endpoint}/${id}`).pipe(
            takeUntilDestroyed(this.destroyRef),
            tap(() => this.emitChange(null)),
            catchError((error: HttpErrorResponse) => this.handleError(error)),
        );
    }
}
